/**
 * セキュリティ・サニタイズユーティリティ
 * 外部通信を行わないため、純粋な入力バリデーションとして機能します。
 */

import DOMPurify from 'dompurify';

/**
 * XSS 対策のための入力サニタイズ
 */
export const sanitizeInput = (input: string): string => {
  if (typeof input !== 'string') return '';
  
  const cleaned = DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: []
  });
  
  return cleaned.trim().slice(0, 500); // 長さ制限
};

/**
 * 習慣データのバリデーションとサニタイズ (IndexedDB スキーマ準拠)
 */
export const sanitizeHabitData = (habit: any, mode: 'create' | 'update' = 'create'): any => {
  if (mode === 'update') {
    const sanitized: any = {};
    
    if (habit.name !== undefined) {
      const n = sanitizeInput(habit.name);
      if (n.length > 0) sanitized.name = n;
    }
    
    if (habit.description !== undefined) sanitized.description = sanitizeInput(habit.description || '');
    if (habit.color !== undefined) sanitized.color = habit.color;
    if (habit.icon !== undefined) sanitized.icon = habit.icon;
    if (habit.isActive !== undefined) sanitized.isActive = !!habit.isActive;
    if (habit.isArchived !== undefined) sanitized.isArchived = habit.isArchived ? 1 : 0;
    if (habit.displayOrder !== undefined) sanitized.displayOrder = habit.displayOrder;
    if (habit.repeatDays !== undefined) sanitized.repeatDays = habit.repeatDays;
    if (habit.goalType !== undefined) sanitized.goalType = habit.goalType;
    
    if (habit.weeklyGoal !== undefined && habit.goalType === 'weekly') {
      sanitized.weeklyGoal = Math.max(1, Math.min(habit.weeklyGoal, 7));
    }
    
    if (habit.reminderEnabled !== undefined) sanitized.reminderEnabled = habit.reminderEnabled ? 1 : 0;
    if (habit.reminderTime !== undefined) sanitized.reminderTime = habit.reminderTime;
    
    return sanitized;
  }

  return {
    ...habit,
    name: sanitizeInput(habit.name || '無題の習慣'),
    description: sanitizeInput(habit.description || ''),
    color: habit.color || '#4F46E5',
    isArchived: 0,
    displayOrder: habit.displayOrder || 0,
    goalType: habit.goalType || 'continuous',
    weeklyGoal: habit.goalType === 'weekly' ? Math.max(1, Math.min(habit.weeklyGoal || 3, 7)) : undefined,
    reminderEnabled: habit.reminderEnabled ? 1 : 0,
    reminderTime: habit.reminderTime || '09:00',
  };
};

/**
 * クリック連打防止のためのレートリミッター
 */
class RateLimiter {
  private lastAction: Map<string, number> = new Map();
  private readonly minInterval: number;

  constructor(minIntervalMs: number = 500) {
    this.minInterval = minIntervalMs;
  }

  canPerformAction(actionKey: string): boolean {
    const now = Date.now();
    const lastTime = this.lastAction.get(actionKey) || 0;
    
    if (now - lastTime < this.minInterval) return false;
    
    this.lastAction.set(actionKey, now);
    return true;
  }
}

export const rateLimiter = new RateLimiter(500);

/**
 * 安全な UUID 生成
 */
export const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * 日付形式のバリデーション (YYYY-MM-DD)
 */
export const validateDate = (date: string): boolean => {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(date)) return false;
  const parsed = new Date(date);
  return !isNaN(parsed.getTime());
};
